import React, { useState, useCallback, useMemo } from 'react';
import Table from '../../components/common/Table';
import { AccountsPackItemDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { FileViewer } from '../common/FileViewer';
import { View } from 'lucide-react';
import { useFileApi } from '../../hooks/useFileApi';

interface AccountsPackItemTableProps {
  data: AccountsPackItemDTO[];
  thumbnailUrls?: Record<string, string>;
  handleEdit: (row: AccountsPackItemDTO) => void;
  handleDelete: (row: AccountsPackItemDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}

// Helper function to check if file is an image
const isImageFile = (filename?: string): boolean => {
  if (!filename) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
  return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
};

const AccountsPackItemTable: React.FC<AccountsPackItemTableProps> = ({
  data,
  thumbnailUrls,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
}) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    url: string;
    fileName: string;
  } | null>(null);
  const { getFileUrl } = useFileApi();

  const handleViewFile = useCallback(async (item: AccountsPackItemDTO) => {
    if (!item.fileRecordId) {
      console.error('No file record ID available');
      return;
    }

    try {
      const response = await getFileUrl(item.fileRecordId);
      if (response && response.data && response.success) {
        setSelectedFile({
          url: response.data.url || '',
          fileName: item.fileRecord?.fileName || 'File',
        });
        setViewerOpen(true);
      }
    } catch (error) {
      console.error('Error getting file URL:', error);
    }
  }, [getFileUrl]);

  const handleCloseViewer = useCallback(() => {
    setViewerOpen(false);
    setSelectedFile(null);
  }, []);

  const viewerProps = useMemo(() => {
    if (!viewerOpen || !selectedFile) return null;
    return {
      fileName: selectedFile.fileName,
      sasUrl: selectedFile.url,
      onClose: handleCloseViewer,
    };
  }, [viewerOpen, selectedFile, handleCloseViewer]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const columns = useMemo(() => {
    const cols: any[] = [];
    
    if (selectable) {
      cols.push({
        label: (
          <input
            type="checkbox"
            checked={selectedIds.length === data.length && data.length > 0}
            onChange={(e) => {
              if (e.target.checked) {
                onSelectionChange?.(data.map(item => item.id || '').filter(Boolean));
              } else {
                onSelectionChange?.([]);
              }
            }}
            className="rounded border-gray-300 dark:border-gray-600"
          />
        ),
        key: 'select',
        width: '50px',
        render: (row: AccountsPackItemDTO) => (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id || '')}
            onChange={(e) => {
              const id = row.id || '';
              if (e.target.checked) {
                onSelectionChange?.([...selectedIds, id]);
              } else {
                onSelectionChange?.(selectedIds.filter(i => i !== id));
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="rounded border-gray-300 dark:border-gray-600"
          />
        ),
      });
    }

    cols.push(
        {
          label: 'Preview',
          key: 'thumbnail',
          width: '80px',
          render: (row: AccountsPackItemDTO) => {
            const thumbnailUrl = thumbnailUrls?.[row.id || ''];
            const fileName = row.fileRecord?.fileName;
            
            if (thumbnailUrl && isImageFile(fileName)) {
              return (
                <img 
                  src={thumbnailUrl} 
                  alt={fileName || 'Thumbnail'}
                  className="h-12 w-12 object-cover rounded"
                />
              );
            } else if (thumbnailUrl) {
              // Show PDF/document icon
              return (
                <div className="h-12 w-12 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded">
                  <svg className="h-8 w-8 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                </div>
              );
            } else {
              // Loading placeholder
              return <div className="h-12 w-12 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>;
            }
          },
        },
        {
          label: 'Email Subject',
          key: 'emailSubject',
        },
        {
          label: 'Email From',
          key: 'emailFrom',
        },
        {
          label: 'File Name',
          key: 'fileRecord',
          render: (row: AccountsPackItemDTO) => (
            <span 
              onClick={() => handleViewFile(row)}
              className="text-secondary dark:text-secondary hover:text-secondary dark:hover:text-secondary cursor-pointer hover:underline"
            >
              {row.fileRecord?.fileName || '-'}
            </span>
          ),
        },
        {
          label: 'Period Year',
          key: 'periodYear',
        },
        {
          label: 'Period Month',
          key: 'periodMonth',
          render: (row: AccountsPackItemDTO) => {
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
              'July', 'August', 'September', 'October', 'November', 'December'];
            return <span>{row.periodMonth ? monthNames[row.periodMonth - 1] : '-'}</span>;
          },
        },
        {
          label: 'Processed',
          key: 'processedUtc',
          format: (value: Date) => value ? convertToUserTime(value.toString()) : '-',
        },
        {
          label: 'Added',
          key: 'createdUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        }
      );

    return cols;
  }, [selectable, selectedIds, data, onSelectionChange, thumbnailUrls, handleViewFile]);

  return (
    <>
    <Table
      groupBy={['periodYear', 'periodMonth']}
      groupHeaderRender={(group: string, rows: any[]) => {
        const [year, month] = group.split(' - ');
        const monthName = monthNames[parseInt(month) - 1] || month;
        return (
          <div className="flex justify-between items-center w-full">
            <span className="font-semibold">{monthName} {year}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {rows.length} {rows.length === 1 ? 'item' : 'items'}
            </span>
          </div>
        );
      }}
      columns={columns}
      data={data}
      actions={{
        onEdit: handleEdit,
        onDelete: handleDelete,
        extra: (row: AccountsPackItemDTO) => [
          {
            label: 'View File',
            onClick: () => handleViewFile(row),
            icon: <View className="w-4 h-4" />,
          },
        ],
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />

    {viewerProps && <FileViewer {...viewerProps} />}
    </>
  );
};

export default React.memo(AccountsPackItemTable);
